/**
 * LocationDeleteInfo — qué pasa al borrar una ubicación, antes de confirmar.
 *
 * Lo usan el diálogo del listado y el de la ficha con los datos de
 * `GET /api/locations/<id>/delete-preview`. La idea es que nadie pulse
 * «Eliminar» pensando que se lleva por delante los equipos:
 *
 *  · los CIs (racks, equipos, chasis, estaciones…) NO se borran: se quedan
 *    SIN UBICACIÓN, y aquí se dice cuántos son y de qué tipo;
 *  · las sub-ubicaciones tampoco: suben un nivel (o quedan en la raíz);
 *  · las notas y enlaces de documentación de la ficha sí se van con ella.
 */

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/** Orden en el que se listan los tipos (de lo más gordo a lo más fino). */
const ORDEN = [
  'racks',
  'rack_elements',
  'chassis',
  'workstations',
  'printers',
  'network_peripherals',
  'ip_phones',
  'wall_outlets',
  'wifi_access_points',
  'isp_demarc_points',
  'ipam_subnets',
];

/** Clave i18n de cada tipo + texto de reserva en español. */
const ETIQUETAS = {
  racks: ['rack.plural', 'racks'],
  rack_elements: ['element.plural', 'equipos'],
  chassis: ['chassis.plural', 'chasis'],
  workstations: ['ws.plural', 'dispositivos'],
  printers: ['printer.plural', 'impresoras'],
  network_peripherals: ['peripheral.plural', 'periféricos'],
  ip_phones: ['phone.plural', 'teléfonos'],
  wall_outlets: ['outlet.plural', 'tomas de red'],
  wifi_access_points: ['wifi.ap_plural', 'puntos de acceso'],
  isp_demarc_points: ['isp.demarc_plural', 'puntos de demarcación'],
  ipam_subnets: ['ipam.subnet_plural', 'subredes IPAM'],
};

export default function LocationDeleteInfo({ info, className = '' }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);

  if (!info) return null;

  const elementos = info.elements || {};
  const total = info.elements_total || 0;
  const hijas = info.children || [];
  const notas = info.notes || 0;
  const enlaces = info.doc_links || 0;

  const filas = ORDEN
    .filter((clave) => (elementos[clave] || 0) > 0)
    .map((clave) => ({
      clave,
      total: elementos[clave],
      etiqueta: tr(ETIQUETAS[clave][0], ETIQUETAS[clave][1]),
    }));
  // Tipos que el backend añada en el futuro y aquí no estén: se muestran igual.
  Object.keys(elementos)
    .filter((clave) => !ORDEN.includes(clave) && (elementos[clave] || 0) > 0)
    .forEach((clave) => filas.push({ clave, total: elementos[clave], etiqueta: clave }));

  return (
    <div className={`list-hint ${className}`.trim()} style={{ marginTop: 10 }}>
      <p style={{ margin: '4px 0' }}>
        {total > 0
          ? tr(
            'location.delete_no_elements',
            'Los equipos y elementos no se borran: se quedarán sin ubicación.',
          )
          : tr(
            'location.delete_no_elements_none',
            'No hay equipos ni elementos colgando de esta ubicación.',
          )}
      </p>

      {filas.length > 0 && (
        <ul style={{ margin: '4px 0 8px 18px', padding: 0 }}>
          {filas.map((fila) => (
            <li key={fila.clave}>
              {fila.etiqueta}: {fila.total}
            </li>
          ))}
        </ul>
      )}

      {hijas.length > 0 && (
        <p style={{ margin: '4px 0' }}>
          {hijas.length} {tr('location.children', 'sub-ubicaciones')}{' '}
          {info.parent
            ? `${tr('location.delete_children_to_parent', 'pasarán a colgar de')} ${info.parent.name}`
            : tr('location.delete_children_to_root', 'quedarán en la raíz del árbol')}
        </p>
      )}

      {(notas > 0 || enlaces > 0) && (
        <p style={{ margin: '4px 0' }}>
          {tr(
            'location.delete_notes',
            'Se eliminarán también sus notas y enlaces de documentación.',
          )}
        </p>
      )}
    </div>
  );
}
